import { router, Link } from "@inertiajs/react";
import {
  Link as CLink,
  Button,
  Container,
  Card,
  Text,
  Badge,
  VStack,
  HStack,
  Heading,
  DataList,
  Table,
  Input,
  Stack,
  Separator,
  IconButton
} from "@chakra-ui/react";
import { Event, Player, Game } from "@/types";
import { formatDate, formatTime, prefectures } from "@/lib/utils";
import { player_path, new_event_game_path, join_event_path, leave_event_path, edit_event_path, edit_event_game_path, event_game_path } from "@/routes";
import { useAuth } from "@/hooks/useAuth";
import Head from "@/components/Head";
// import { LuPencil, LuTrash } from "react-icons/lu";

export interface PlayerRanking {
  player: Pick<Player, "display_name" | "player_id">;
  wins: number;
  losses: number;
  total_points_scored: number;
  total_points_lost: number;
  rating_change: number;
  total_game_count: number;
}

export interface EventPlayer {
  player: Pick<Player, "display_name" | "player_id">;
  pre_event_rating_score: number;
  after_event_rating_score: number | null;
}

interface Props {
  event: Event;
  players: EventPlayer[];
  rankings: PlayerRanking[];
  games: Game[];
  is_participating: boolean;
}

export default function Show({ event, players, rankings, games, is_participating }: Props) {
  const currentPlayer = useAuth().player;

  const handleJoinEvent = () => {
    if (confirm("このイベントに参加しますか？")) {
      router.post(join_event_path(event.id));
    }
  };

  const handleLeaveEvent = () => {
    if (confirm("本当にこのイベントから辞退しますか？")) {
      router.delete(leave_event_path(event.id));
    }
  };

  const handleGameLink = () => {
    window.location.href = new_event_game_path(event.id);
  };

  const handleEditEvent = () => {
    router.visit(edit_event_path(event.id));
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(event.share_url);
  };

  const handleEditGame = (gameId: number) => {
    router.visit(edit_event_game_path(event.id, gameId));
  };

  const handleDeleteGame = (gameId: number) => {
    if (confirm("この試合結果を削除しますか？")) {
      router.delete(event_game_path(event.id, gameId));
    }
  };

  const handleStartEvent = () => {
    if (confirm("このイベントを開催しますか？")) {
      router.post(`/events/${event.id}/start`);
    }
  };

  const handleFinishEvent = () => {
    if (confirm("このイベントを終了しますか？")) {
      router.post(`/events/${event.id}/finish`);
    }
  };

  return (
    <>
      <Head title={event.name} robots={event.public_status === "published"} />

      <Container maxW="6xl" px={{ base: 0, sm: 4 }} py={4}>
        <VStack gap={4} align="stretch">
          {/* 参加・辞退ボタン（ログイン済みの場合） */}
          {currentPlayer && (
            <>
              <Card.Root>
                <Card.Header pb={2}>
                  <Heading as="h1" size="md">イベント詳細</Heading>
                </Card.Header>
                <Card.Body pt={0}>
                  <HStack gap={2} wrap={"wrap"}>
                    {is_participating && event.can_edit_status ? (
                      <>
                        <Button colorPalette="blue" size="sm" onClick={handleGameLink}>
                          試合結果を入力する
                        </Button>
                        <Button colorPalette="red" variant="outline" size="sm" onClick={handleLeaveEvent}>
                          イベントから辞退する
                        </Button>
                      </>
                    ) : !is_participating && event.can_edit_status ? (
                      <Button colorPalette="blue" size="sm" onClick={handleJoinEvent}>
                        イベントに参加する
                      </Button>
                    ) : null}
                    {event.can_edit && (
                      <Button colorPalette="green" variant="outline" size="sm" onClick={handleEditEvent}>
                        イベントを編集
                      </Button>
                    )}
                  </HStack>
                </Card.Body>
              </Card.Root>

              <Separator />
            </>
          )}

          {/* 基本情報と参加者を横並び */}
          <Stack direction={{ base: "column", md: "row" }} gap={4} align="start">
            {/* 基本情報・共有リンク */}
            <VStack gap={4} align="stretch" flex={1} w="full">
              {/* イベント基本情報 */}
              <Card.Root w="full">
                <Card.Header px={{ base: 4, sm: 6 }} pb={1}>
                  <Heading size="sm">基本情報</Heading>
                </Card.Header>
                <Card.Body px={{ base: 4, sm: 6 }} pt={2}>
                  <DataList.Root size="sm">
                    <DataList.Item>
                      <DataList.ItemLabel minW="80px">イベント名</DataList.ItemLabel>
                      <DataList.ItemValue>{event.name}</DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.ItemLabel minW="80px">開催状況</DataList.ItemLabel>
                      <DataList.ItemValue>
                        {event.can_edit_status && event.can_edit ? (
                          (() => {
                            if (event.status === "scheduled") {
                              return (
                                <Button colorPalette="green" size="xs" onClick={handleStartEvent}>
                                  開催する
                                </Button>
                              );
                            } else if (event.status === "ongoing") {
                              return (
                                <Button colorPalette="red" size="xs" onClick={handleFinishEvent}>
                                  終了する
                                </Button>
                              );
                            }
                          })()
                        ) : !event.can_edit_status ? (
                          <Badge size="sm" colorPalette="gray">
                            終了したイベント
                          </Badge>
                        ) : (
                          <Badge size="sm" colorPalette={event.status === "scheduled" ? "blue" : "green"}>
                            {event.status_text}
                          </Badge>
                        )}
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.ItemLabel minW="80px">公開ステータス</DataList.ItemLabel>
                      <DataList.ItemValue>
                        <Badge size="sm" colorPalette={event.status === "published" ? "green" : event.status === "active" ? "blue" : "gray"}>
                          {event.public_status_text}{" "}
                        </Badge>
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.ItemLabel minW="80px">主催者</DataList.ItemLabel>
                      <DataList.ItemValue fontSize="sm">
                        {event.creator?.display_name || "不明"} ({event.creator?.player_id || "N/A"})
                      </DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                      <DataList.ItemLabel minW="80px">期間</DataList.ItemLabel>
                      <DataList.ItemValue fontSize="sm">
                        {formatDate(event.start_at)} {formatTime(event.start_at)} 〜 {formatDate(event.end_at)} {formatTime(event.end_at)}
                      </DataList.ItemValue>
                    </DataList.Item>
                    {event.prefecture && (
                      <DataList.Item>
                        <DataList.ItemLabel minW="80px">都道府県</DataList.ItemLabel>
                        <DataList.ItemValue fontSize="sm">
                          {prefectures[event.prefecture] || event.prefecture}
                        </DataList.ItemValue>
                      </DataList.Item>
                    )}
                  </DataList.Root>
                </Card.Body>
              </Card.Root>

              {/* 共有リンクセクション */}
              <Card.Root w="full">
                <Card.Header px={{ base: 4, sm: 6 }} pb={1}>
                  <Heading size="sm">共有リンク</Heading>
                </Card.Header>
                <Card.Body px={{ base: 4, sm: 6 }} pt={2}>
                  <VStack gap={2} align="stretch">
                    <HStack gap={2}>
                      <Input value={event.share_url} readOnly onClick={e => e.currentTarget.select()} flex={1} size="sm" />
                      <Button onClick={handleCopyShareLink} colorPalette="blue" size="sm">
                        コピー
                      </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">
                      {event.public_status === "published"
                        ? "このイベントは公開されており、誰でもアクセスできます。"
                        : "このリンクを知っている人のみアクセスできます。"}
                    </Text>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </VStack>

            {/* 参加者 */}
            <VStack gap={4} align="stretch" flex={1} w="full">
              {/* 参加者セクション */}
              <Card.Root w="full">
                <Card.Header px={{ base: 4, sm: 6 }} pb={1}>
                  <Heading size="sm">参加者 ({players.length}名)</Heading>
                </Card.Header>
                <Card.Body px={{ base: 4, sm: 6 }} pt={2}>
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>プレイヤー</Table.ColumnHeader>
                        <Table.ColumnHeader>
                          レーティング
                          <br />
                          (参加前)
                        </Table.ColumnHeader>
                        <Table.ColumnHeader>
                          レーティング
                          <br />
                          (試合後)
                        </Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {players.map(player => (
                        <Table.Row key={player.player.player_id}>
                          <Table.Cell>
                            <CLink as={Link} href={player_path(player.player.player_id)}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {player.player.display_name}{" "}
                                <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">
                                  ({player.player.player_id})
                                </Text>
                              </Text>
                            </CLink>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette="gray" size="sm">
                              {player.pre_event_rating_score}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Stack direction="row" align="center">
                              {player.after_event_rating_score === null ? (
                                <></>
                              ) : player.after_event_rating_score > player.pre_event_rating_score ? (
                                <>
                                  <Badge colorPalette="blue" size="sm">
                                    {player.after_event_rating_score}
                                  </Badge>
                                  <Text as="span" color="blue.600" fontWeight="semibold" fontSize="sm">
                                    +{player.after_event_rating_score - player.pre_event_rating_score}
                                  </Text>
                                </>
                              ) : player.after_event_rating_score < player.pre_event_rating_score ? (
                                <>
                                  <Badge colorPalette="red" size="sm">
                                    {player.after_event_rating_score}
                                  </Badge>
                                  <Text color="red.600" fontWeight="semibold" fontSize="sm">
                                    -{player.pre_event_rating_score - player.after_event_rating_score}
                                  </Text>
                                </>
                              ) : (
                                <>
                                  <Badge colorPalette="gray" size="sm">
                                    {player.after_event_rating_score}
                                  </Badge>
                                  <Text color="gray.500" fontSize="sm">
                                    0
                                  </Text>
                                </>
                              )}
                            </Stack>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Card.Body>
              </Card.Root>
            </VStack>
          </Stack>

          <Separator />

          {/* ランキングと対戦結果を横並び */}
          <Stack direction={{ base: "column", md: "row" }} gap={4} align="start">
            {/* ランキングセクション */}
            <Card.Root flex={1} w="full">
              <Card.Header px={{ base: 4, sm: 6 }} pb={1}>
                <Heading size="sm">ランキング</Heading>
              </Card.Header>
              <Card.Body px={{ base: 4, sm: 6 }} pt={2}>
                {rankings.length > 0 ? (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader>順位</Table.ColumnHeader>
                        <Table.ColumnHeader>プレイヤー</Table.ColumnHeader>
                        <Table.ColumnHeader>勝敗</Table.ColumnHeader>
                        <Table.ColumnHeader>試合数</Table.ColumnHeader>
                        <Table.ColumnHeader>得失点</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {rankings.map((stats, index) => (
                        <Table.Row key={stats.player.player_id}>
                          <Table.Cell>
                            <Badge size="sm" colorPalette={index === 0 ? "yellow" : index === 1 ? "gray" : index === 2 ? "orange" : "gray"}>
                              {index + 1}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <CLink as={Link} href={player_path(stats.player.player_id)}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {stats.player.display_name}
                              </Text>
                            </CLink>
                          </Table.Cell>
                          <Table.Cell>
                            <HStack gap={1}>
                              <Badge colorPalette="green" size="sm">
                                {stats.wins}
                              </Badge>
                              <Text fontSize="xs">-</Text>
                              <Badge colorPalette="red" size="sm">
                                {stats.losses}
                              </Badge>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell fontSize="sm">{stats.total_game_count}</Table.Cell>
                          <Table.Cell fontSize="sm">
                            {stats.total_points_scored}-{stats.total_points_lost}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text color="gray.500" fontSize="sm">
                    まだ対戦結果がありません。
                  </Text>
                )}
              </Card.Body>
            </Card.Root>

            {/* 対戦結果セクション */}
            <Card.Root flex={1} w="full">
              <Card.Header px={{ base: 4, sm: 6 }} pb={1}>
                <Heading size="sm">対戦結果</Heading>
              </Card.Header>
              <Card.Body px={{ base: 4, sm: 6 }} pt={2}>
                {games.length > 0 ? (
                  <Table.Root size="sm">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeader w="28%">プレイヤー1</Table.ColumnHeader>
                        <Table.ColumnHeader w="17%" textAlign="center" />
                        <Table.ColumnHeader w="28%">プレイヤー2</Table.ColumnHeader>
                        <Table.ColumnHeader w="17%">結果</Table.ColumnHeader>
                        <Table.ColumnHeader w="10%">操作</Table.ColumnHeader>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {games.map(game => (
                        <Table.Row key={game.id}>
                          <Table.Cell>
                            <CLink as={Link} href={player_path(game.player1.player_id)}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {game.player1.display_name}
                              </Text>
                            </CLink>
                            <HStack gap={1} mt={0.5}>
                              <Text fontSize="xs" color="gray.500">
                                {game.player1_rating_score}
                              </Text>
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color={game.player1_rating_diff > 0 ? "blue.600" : game.player1_rating_diff < 0 ? "red.600" : "gray.500"}
                              >
                                {game.player1_rating_diff > 0 ? `+${game.player1_rating_diff}` : game.player1_rating_diff}
                              </Text>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            <Text fontWeight="bold" color="gray.600" fontSize="sm">
                              vs
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <CLink as={Link} href={player_path(game.player2.player_id)}>
                              <Text fontWeight="semibold" fontSize="sm">
                                {game.player2.display_name}
                              </Text>
                            </CLink>
                            <HStack gap={1} mt={0.5}>
                              <Text fontSize="xs" color="gray.500">
                                {game.player2_rating_score}
                              </Text>
                              <Text
                                fontSize="xs"
                                fontWeight="semibold"
                                color={game.player2_rating_diff > 0 ? "blue.600" : game.player2_rating_diff < 0 ? "red.600" : "gray.500"}
                              >
                                {game.player2_rating_diff > 0 ? `+${game.player2_rating_diff}` : game.player2_rating_diff}
                              </Text>
                            </HStack>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette="gray" size="sm" minW="60px" textAlign="center" display="inline-block" py={"1"}>
                              {game.player1_point} - {game.player2_point}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell textAlign="center">
                            {game.can_edit ? (
                              <HStack gap={1} justify="center">
                                <IconButton size="xs" variant="outline" onClick={() => handleEditGame(game.id)} title="編集">
                                  編集
                                </IconButton>
                                <IconButton size="xs" variant="outline" colorPalette="red" onClick={() => handleDeleteGame(game.id)} title="削除">
                                  削除
                                </IconButton>
                              </HStack>
                            ) : (
                              <Badge size="sm" colorPalette="red">
                                不可
                              </Badge>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text color="gray.500" fontSize="sm">
                    まだ対戦結果がありません。
                  </Text>
                )}
              </Card.Body>
            </Card.Root>
          </Stack>
        </VStack>
      </Container>
    </>
  );
}
